const MONTH_KEY_REGEX = /^(\d{4})-(\d{2})$/;

export const normalizeMonthKey = (value) => {
  if (!value && value !== 0) return null;
  const raw = String(value).trim();
  const match = raw.match(MONTH_KEY_REGEX);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return `${year}-${String(month).padStart(2, '0')}`;
};

export const monthKeyFromDateValue = (value) => {
  if (!value && value !== 0) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const direct = normalizeMonthKey(trimmed);
    if (direct) return direct;
    if (trimmed.length >= 7) {
      const prefix = normalizeMonthKey(trimmed.slice(0, 7));
      if (prefix) return prefix;
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

export const resolveMonthKey = (candidate, fallbackValue = null) => {
  const preferred = monthKeyFromDateValue(candidate);
  if (preferred) return preferred;
  return monthKeyFromDateValue(fallbackValue);
};

export const isDateInMonth = (value, monthKey) => {
  const normalizedMonth = normalizeMonthKey(monthKey);
  if (!normalizedMonth) return false;
  return resolveMonthKey(value) === normalizedMonth;
};

export const formatMonthKeyLabel = (monthKey) => {
  const normalizedMonth = normalizeMonthKey(monthKey);
  if (!normalizedMonth) return '';
  const [year, month] = normalizedMonth.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const formatMonthKeyContextLabel = (monthKey, { currentMonthKey = getCurrentMonthKey() } = {}) => {
  const normalizedMonth = normalizeMonthKey(monthKey);
  if (!normalizedMonth) return '';

  const [year, month] = normalizedMonth.split('-').map(Number);
  const currentYear = Number(String(normalizeMonthKey(currentMonthKey) || '').slice(0, 4));

  return new Date(year, month - 1, 1).toLocaleDateString('en-US', (
    Number.isFinite(currentYear) && currentYear === year
      ? { month: 'short' }
      : { month: 'short', year: 'numeric' }
  ));
};

export const shiftMonthKey = (monthKey, direction) => {
  const normalizedMonth = normalizeMonthKey(monthKey);
  if (!normalizedMonth) return null;
  const [year, month] = normalizedMonth.split('-').map(Number);
  const offset = direction === 'prev' ? -1 : 1;
  const date = new Date(year, month - 1 + offset, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export const getCurrentMonthKey = () => monthKeyFromDateValue(new Date());

export const getAvailableMonthKeys = (items = []) => {
  const currentMonth = getCurrentMonthKey();
  const months = new Set();

  items.forEach((item) => {
    const monthKey = monthKeyFromDateValue(item?.date ?? item?.transaction_date ?? item?.month);
    if (!monthKey) return;
    if (currentMonth && monthKey > currentMonth) return;
    months.add(monthKey);
  });

  return [...months].sort();
};

export const getLatestAvailableMonth = (availableMonths = []) => {
  const normalizedMonths = [...new Set((Array.isArray(availableMonths) ? availableMonths : []).map(normalizeMonthKey).filter(Boolean))].sort();
  return normalizedMonths[normalizedMonths.length - 1] || null;
};

export const getAdjacentAvailableMonth = (selectedMonth, availableMonths = [], direction = 'prev') => {
  const normalizedMonth = normalizeMonthKey(selectedMonth);
  if (!normalizedMonth || !Array.isArray(availableMonths) || availableMonths.length === 0) return null;

  const uniqueMonths = [...new Set(availableMonths.map(normalizeMonthKey).filter(Boolean))].sort();
  if (direction === 'prev') {
    for (let index = uniqueMonths.length - 1; index >= 0; index -= 1) {
      if (uniqueMonths[index] < normalizedMonth) return uniqueMonths[index];
    }
    return null;
  }

  for (let index = 0; index < uniqueMonths.length; index += 1) {
    if (uniqueMonths[index] > normalizedMonth) return uniqueMonths[index];
  }
  return null;
};

export const getMonthNavigationState = ({ selectedMonth, items = [] }) => {
  const availableMonths = getAvailableMonthKeys(items);
  const previousMonth = getAdjacentAvailableMonth(selectedMonth, availableMonths, 'prev');
  const nextMonth = getAdjacentAvailableMonth(selectedMonth, availableMonths, 'next');
  return {
    availableMonths,
    previousMonth,
    nextMonth,
    showPrevMonth: Boolean(previousMonth),
    showNextMonth: Boolean(nextMonth),
  };
};
